import { AlertColor, Box, Button, Dialog } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { palette } from '../..';
import { PopUpSpinner, SuccessFailureDialog } from '../CommonFunctions/DialogComponents';
import RichTreeViewControl from '../Controls/RichTreeViewControl';
import {
  ConvertListOfListToDataConnectionRichTreeViewList,
  ConvertListOfListToRichTreeViewList,
  findNodeWithParent,
  isNameAllowed,
  Node,
} from '../CommonFunctions/CommonFunctions';
import { ConnectionInfo } from './Data';
import Logger from '../../Logger';
import { RegisterOrUpdateDBConnection, testDBConnection } from '../../api/dbConnection';
import { deleteContent, getAllSubworkspace, getContentDependencies } from '../../api/workspace';
import { useNavigate, useParams } from 'react-router-dom';
import { contentTypes } from '../CommonFunctions/aliases';
import HoverButton from '../Buttons/HoverButton';
interface ActionButtonsProps {
  viewMode: boolean;
  setViewMode: (viewMode: boolean) => void;
  disableEdit: boolean;
  info: ConnectionInfo;
  update?: boolean;
  workspaceId?: string;
}
const ActionButtons = ({
  viewMode,
  setViewMode,
  disableEdit,
  update = false,
  info,
  workspaceId,
}: ActionButtonsProps) => {
  const { connectionId } = useParams();
  const [showWorksapace, setShowWorkspace] = useState(false);
  const [workspaceList, setWorkspaceList] = useState<any[]>([]);
  const [showDependency, setShowDependency] = useState(false);
  const [dependencyList, setDependencyList] = useState<any[]>([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notify, setNotify] = useState<{
    message: string;
    severity: AlertColor;
  } | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const isNonEmpty = (val: string | File | null | undefined): boolean =>
    val !== undefined && val !== null && val !== '';

  const isValidCredential = useMemo(() => {
    if (!info || !info.vendor) return false;

    const nameValid =
      isNameAllowed(info.connectionName) && info.connectionName && info.connectionName.length <= 40;

    if (!nameValid) return false;

    const vendor = info.vendor.toLowerCase();

    const validators: Record<string, () => boolean> = {
      sqlserver: () =>
        [info.server, info.port, info.database, info.username, info.password].every(isNonEmpty),

      mysql: () =>
        [info.server, info.port, info.database, info.username, info.password].every(isNonEmpty),

      postgresql: () =>
        [info.server, info.port, info.database, info.username, info.password].every(isNonEmpty),

      teradata: () =>
        [info.server, info.port, info.database, info.username, info.password].every(isNonEmpty),

      db2: () =>
        [info.server, info.port, info.database, info.username, info.password].every(isNonEmpty),

      bigquery: () => isNonEmpty(info.password),

      snowflake: () => [info.server, info.username, info.password].every(isNonEmpty),

      motherduck: () => [info.database, info.password].every(isNonEmpty),

      redshift: () => [info.server, info.port, info.database, info.password].every(isNonEmpty),

      amazonathena: () =>
        [info.region, info.s3Location, info.username, info.password].every(isNonEmpty),

      databricks: () =>
        [info.httpPath, info.port, info.database, info.server, info.password].every(isNonEmpty),

      oracle: () =>
        [
          info.server,
          info.port,
          info.database,
          info.username,
          info.password,
          info.keystorePassword,
          info.keystore,
          info.truststorePassword,
          info.truststore,
        ].every(isNonEmpty),

      mariadb: () =>
        [info.server, info.port, info.database, info.password, info.username].every(isNonEmpty),

      saphana: () => [info.server, info.port, info.username, info.password].every(isNonEmpty),

      clickhouse: () =>
        [info.server, info.port, info.database, info.username, info.password].every(isNonEmpty),
    };

    return validators[vendor]?.() ?? false;
  }, [info]);

  const getWorkspaces = async () => {
    try {
      setLoading(true);
      if (workspaceList.length > 0) {
        setShowWorkspace(true);
        return;
      }
      const res = await getAllSubworkspace();
      if (res.isError) {
        setNotify({
          message: res.message,
          severity: 'error',
        });
      } else {
        setWorkspaceList(ConvertListOfListToRichTreeViewList(res.data!));
        setShowWorkspace(true);
      }
    } catch (error) {
      setNotify({
        message: 'Failed to fetch workspaces',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  const handleTest = async (_workspaceId?: string, showSuccess = true) => {
    if (isValidCredential) {
      try {
        setLoading(true);
        const res = await testDBConnection(
          _workspaceId ?? workspaceId!,
          info,
          info.vendor === 'oracle',
        );
        if (res.isError) {
          setLoading(false);
          setNotify({
            message: res.message,
            severity: 'error',
          });
        } else {
          if (showSuccess) {
            setNotify({
              message: res.message,
              severity: 'success',
            });
            setTimeout(() => {
              setNotify(null);
            }, 3000);
          }
          return true;
        }
      } catch (error) {
        setNotify({
          message: 'Connection test failed',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    } else {
      Logger('error', 'Invalid CredentialsContainer', 'Please fill out all the fields');
      setNotify({
        message: 'Please fill out all the fields',
        severity: 'warning',
      });
    }
  };
  const handleRegister = async (_worksapceId: string, list: Node[], update = false) => {
    try {
      setLoading(true);
      const testRes = await handleTest(_worksapceId, false);
      if (!testRes) return;

      const res = await RegisterOrUpdateDBConnection(
        _worksapceId,
        info,
        info.vendor === 'oracle',
        update,
        /**for oracle update we need connection id */
        connectionId,
      );
      if (res.isError) {
        setNotify({
          message: res.message,
          severity: 'error',
        });
      } else {
        setShowWorkspace(false);
        setNotify({
          message: res.message,
          severity: 'success',
        });
        setTimeout(() => {
          const details = findNodeWithParent(list, _worksapceId);
          if (!details) {
            navigate('/');
            setTimeout(() => {
              setNotify(null);
              navigate(`/`);
            }, 2000);
            return;
          }
          localStorage.setItem('workspace', details.parentLabel ?? details.label);
          if (details?.parentId) {
            localStorage.setItem('parentId', _worksapceId);
            localStorage.setItem('subworkspace', details.label);
            localStorage.setItem('parentWorkspaceId', details.parentId);
            navigate(`/SubWorkspaceDetails/${_worksapceId}`, {
              state: {
                wname: details.parentLabel,
                childWorkspaceName: details.label,
                parentId: details.parentId,
              },
            });
          } else
            navigate(`/workspace/${_worksapceId}?worksapce=${details?.label}`, {
              state: { wname: details?.label },
            });
          setNotify(null);
        }, 3000);
      }
    } catch (error) {
      setNotify({
        message: 'Failed to register DB connection',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  const handleUpdate = async () => {
    try {
      setLoading(true);
      const res = await getAllSubworkspace();
      if (res.isError) {
        setNotify({
          message: res.message,
          severity: 'error',
        });
      } else {
        const list = ConvertListOfListToRichTreeViewList(res.data!);
        handleRegister(workspaceId!, list, true);
      }
    } catch (error) {
      setNotify({
        message: 'Failed to update DB connection',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async () => {
    try {
      setLoading(true);
      const res = await getContentDependencies(
        info.id,
        contentTypes.DataConnection.toString(),
        workspaceId!,
      );
      if (res.error) {
        setNotify({
          message: 'Unable to fetch Dependencies',
          severity: 'error',
        });
        return;
      }
      if (!res.hasDependency) {
        const res = await deleteContent(info.id, workspaceId!, contentTypes.DataConnection);
        if (res.isError) {
          setNotify({
            message: res.message,
            severity: 'error',
          });
        } else {
          setNotify({
            message: res.message,
            severity: 'success',
          });
          setTimeout(() => {
            navigate(-1);
          }, 3000);
        }
      } else {
        const treeViewDependency = ConvertListOfListToDataConnectionRichTreeViewList(
          res.dependencies,
          'dataset',
        );
        setDependencyList(treeViewDependency);
        setShowDependency(true);
      }
    } catch (error) {
      setNotify({
        message: 'Failed to delete DB connection',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: '35%',
        display: 'flex',
        justifyContent: 'space-between',
        paddingInline: '0.5rem',
      }}
    >
      {loading && (
        <PopUpSpinner
          show={loading}
          paperProps={{
            sx: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
        />
      )}
      {showDependency && (
        <RichTreeViewControl
          isWarning={true}
          list={dependencyList}
          showControls={false}
          showInPopup={true}
          handleCloseButtonClick={(e: any) => {
            setShowDependency(false);
          }}
          title={[`Can't delete this DB connection.`, 'Here is the list of dependencies.']}
          hasMultipleTitle={true}
        />
      )}
      {showWorksapace && (
        <RichTreeViewControl
          currentWorkspace={workspaceId}
          proceedButtonName={'Save'}
          list={workspaceList}
          title={'Select a Workspace'}
          showInPopup={showWorksapace}
          handleCloseButtonClick={(e: any) => {
            setShowWorkspace(false);
          }}
          handleProceedButtonClick={(selectedWorkspaceID: string, list: any) => {
            if (!selectedWorkspaceID) {
              setNotify({
                message: 'Select a workspace.',
                severity: 'error',
              });
              return;
            }

            handleRegister(selectedWorkspaceID, list as Node[]);
          }}
        ></RichTreeViewControl>
      )}
      {viewMode ? (
        <>
          <HoverButton
            text="Delete"
            color="gray"
            hoverColor="white"
            backgroundColor="white"
            hoverBackgroundColor="red"
            transitionTime="0.2s"
            sx={{
              border: '1px solid gray',
              opacity: disableEdit ? 0.5 : 1,
              pointerEvents: disableEdit ? 'none' : 'auto',
              '&:hover': {
                borderColor: 'red',
              },
            }}
            disabled={disableEdit}
            onClick={() => {
              if (!disableEdit) setDeleteConfirmation(true);
            }}
          />

          <HoverButton
            text="Edit"
            color="gray"
            hoverColor="white"
            backgroundColor="white"
            hoverBackgroundColor="rgb(175,153,219)"
            transitionTime="0.2s"
            sx={{
              border: '1px solid gray',
              opacity: disableEdit ? 0.5 : 1,
              pointerEvents: disableEdit ? 'none' : 'auto',
              '&:hover': {
                borderColor: 'rgb(175,153,219)',
              },
            }}
            disabled={disableEdit}
            onClick={() => {
              if (!disableEdit) setViewMode(false);
            }}
          />
        </>
      ) : (
        <>
          <Button
            className="testButton"
            variant="contained"
            disabled={!isValidCredential}
            onClick={() => handleTest()}
            // disabled={btnEnable}
            sx={{
              backgroundColor: disableEdit ? 'rgba(224,224,224,1)' : palette.primary.main,
              lineHeight: 'normal',
              color: palette.secondary.contrastText,
              boxShadow: 'none',
              '&:hover': {
                boxShadow:
                  '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
                backgroundColor: palette.primary.main,
              },
              // marginRight: "-108px",
            }}
          >
            Test
          </Button>

          <Button
            disabled={!isValidCredential}
            onClick={(e) => {
              if (update) handleUpdate();
              else getWorkspaces();
            }}
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: disableEdit ? 'rgba(224,224,224,1)' : palette.secondary.light,
              // marginRight: "5px",
              lineHeight: 'normal',
              color: palette.secondary.contrastText,
              boxShadow: 'none',
              '&:hover': {
                boxShadow:
                  '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
                backgroundColor: palette.secondary.light,
              },
            }}

            // disabled={btnEnable}
          >
            {update ? 'Update' : 'Register'}
          </Button>
        </>
      )}

      <Dialog open={deleteConfirmation}>
        <div className="dbDeleteDialog">
          <div className="dbDeleteDialogMsg">
            Delete DB Connection?
            <br />
            <br />
          </div>
          <div className="dbDeleteDialogBtnContainer">
            <HoverButton
              text="Delete"
              color="red"
              transitionTime="0.2s"
              backgroundColor="secondary.contrastText"
              hoverColor="secondary.contrastText"
              hoverBackgroundColor="red"
              sx={{
                border: '1px solid red',
              }}
              onClick={() => {
                handleDelete();
                setDeleteConfirmation(false);
              }}
            />

            <HoverButton
              text="Cancel"
              color="gray"
              transitionTime="0.2s"
              backgroundColor="secondary.contrastText"
              hoverColor="secondary.contrastText"
              hoverBackgroundColor="gray"
              sx={{
                border: '1px solid gray',
              }}
              onClick={() => setDeleteConfirmation(false)}
            />
          </div>
        </div>
      </Dialog>

      {notify && (
        <SuccessFailureDialog
          show={!!notify}
          message={notify.message}
          severity={notify.severity}
          onClose={() => {
            if (notify.severity === 'error') {
              setNotify(null);
            }
          }}
        />
      )}
    </Box>
  );
};

export default ActionButtons;
